import {
  CLINIC_TIMEZONE,
  addZonedDays,
  addZonedMonths,
  endOfZonedDay,
  startOfZonedDay,
  startOfZonedWeek,
  zonedDateTime,
  zonedInstant,
  zonedYmd,
} from '@/lib/reporting/timezone';

export { CLINIC_TIMEZONE };

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

/** Europe/Copenhagen calendar day, including just after UTC midnight. */
export function clinicDayKey(date: Date): string {
  return zonedYmd(date, CLINIC_TIMEZONE);
}

export function clinicStartOfDay(date: Date): Date {
  return startOfZonedDay(date, CLINIC_TIMEZONE);
}

export function clinicAddDays(date: Date, days: number): Date {
  return addZonedDays(clinicStartOfDay(date), days, CLINIC_TIMEZONE);
}

export function clinicAddMonths(date: Date, months: number): Date {
  return addZonedMonths(clinicStartOfDay(date), months, CLINIC_TIMEZONE);
}

export function clinicEndOfDay(date: Date): Date {
  return endOfZonedDay(date, CLINIC_TIMEZONE);
}

export function clinicMonthBounds(anchor: Date): { start: Date; end: Date } {
  const parts = zonedDateTime(anchor, CLINIC_TIMEZONE);
  return {
    start: zonedInstant(parts.year, parts.month, 1, 0, 0, 0, 0, CLINIC_TIMEZONE),
    end: zonedInstant(parts.year, parts.month + 1, 0, 0, 0, 0, 0, CLINIC_TIMEZONE),
  };
}

export function clinicWeekDays(anchor: Date): Date[] {
  const monday = startOfZonedWeek(anchor, CLINIC_TIMEZONE);
  return Array.from({ length: 7 }, (_, index) => addZonedDays(monday, index, CLINIC_TIMEZONE));
}

/** Six weeks starting on the Copenhagen Monday that contains the 1st. */
export function clinicMonthGridDays(anchor: Date): Date[] {
  const gridStart = clinicWeekDays(clinicMonthBounds(anchor).start)[0] ?? anchor;
  return Array.from({ length: 42 }, (_, index) => clinicAddDays(gridStart, index));
}

/** `YYYY-MM` in Europe/Copenhagen. */
export function clinicMonthKey(date: Date): string {
  return clinicDayKey(date).slice(0, 7);
}

/** JavaScript `Date#getDay` (Sunday = 0) for the Copenhagen weekday. */
export function clinicJsWeekday(date: Date): number {
  return (clinicWeekdayIndex(date) + 1) % 7;
}

/** Monday = 0 … Sunday = 6 in Europe/Copenhagen. */
export function clinicWeekdayIndex(date: Date): number {
  const label = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIMEZONE,
    weekday: 'short',
  }).format(date);
  return WEEKDAY_INDEX[label] ?? 0;
}

export function clinicDayNumber(date: Date): number {
  return zonedDateTime(date, CLINIC_TIMEZONE).day;
}

export function clinicMinutesFromMidnight(date: Date): number {
  const parts = zonedDateTime(date, CLINIC_TIMEZONE);
  return parts.hour * 60 + parts.minute + parts.second / 60;
}

/**
 * Wall-clock minutes on a Copenhagen day. 24:00 is the next midnight,
 * which is the end of a 23:30 slot.
 */
export function clinicInstantFromMinutes(day: Date, minutesFromMidnight: number): Date {
  const parts = zonedDateTime(clinicStartOfDay(day), CLINIC_TIMEZONE);
  const whole = Math.trunc(minutesFromMidnight);
  return zonedInstant(
    parts.year,
    parts.month,
    parts.day,
    Math.floor(whole / 60),
    whole % 60,
    0,
    0,
    CLINIC_TIMEZONE,
  );
}

/**
 * The day shown after Uge → Dag. The instant 2026-11-04T23:30Z is 00:30 on
 * 5 Nov in Copenhagen; a UTC date slice would show 4 Nov.
 */
export function anchorForCalendarView(anchor: Date): Date {
  return clinicStartOfDay(anchor);
}

export function parseClinicDayKey(value: string | null | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const parsed = zonedInstant(year, month, day, 0, 0, 0, 0, CLINIC_TIMEZONE);
  if (clinicDayKey(parsed) !== value) return null;
  return parsed;
}

export function sameClinicDay(a: Date, b: Date): boolean {
  return clinicDayKey(a) === clinicDayKey(b);
}
